import { Collapse, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { FormAwareButtonGroup } from '@/components/form/formAwareInputs/FormAwareButtonGroup';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { Card } from '@/components/layout/Card/Card';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import { COLORS } from '@/styles/tokens/colors';
import { TransferMethod } from '@/types/schema';

import { hasOnlyInsurancePolicies } from '../../dispositiveProvisions.formatting';
import {
  SplitDispositionFormShape,
  SplitDistributionKind,
} from './SplitDispositionModal.types';

const TRANSFER_METHOD_OPTIONS = [
  { display: 'In kind', value: TransferMethod.InKind },
  { display: 'Liquidate', value: TransferMethod.LiquidateToCash },
];

interface SplitAdvancedSettingsCardProps {
  isExpanded: boolean;
  distributionKind: SplitDistributionKind;
}

export function SplitAdvancedSettingsCard({
  isExpanded,
  distributionKind,
}: SplitAdvancedSettingsCardProps) {
  const { control } = useFormContext<SplitDispositionFormShape>();
  const isAsset = distributionKind === 'asset';

  const [insurancePolicyIds, assetStaticIds, assetGroupIds, ownershipStakeIds] =
    useWatch({
      control,
      name: [
        'insurancePolicyIds',
        'assetStaticIds',
        'assetGroupIds',
        'ownershipStakeIds',
      ],
      disabled: !isAsset,
    });

  // Hide transfer toggle when only insurance policies are selected
  const hideTransferToggle = useMemo(() => {
    if (!isAsset) return false;
    return hasOnlyInsurancePolicies({
      insurancePolicies: insurancePolicyIds?.length ?? 0,
      assetStatics: assetStaticIds?.length ?? 0,
      assetGroups: assetGroupIds?.length ?? 0,
      ownershipStakes: ownershipStakeIds?.length ?? 0,
    });
  }, [
    isAsset,
    insurancePolicyIds,
    assetStaticIds,
    assetGroupIds,
    ownershipStakeIds,
  ]);

  const showTransferToggle = isAsset && !hideTransferToggle;

  return (
    <Collapse in={isExpanded}>
      <Card
        variant="filled"
        sx={{
          px: 2,
          py: 1.5,
          border: '1px solid',
          borderColor: COLORS.NAVY[100],
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="body-xs">Advanced settings</Typography>
          {!isAsset && (
            <FormAwareCurrencyInput<SplitDispositionFormShape>
              control={control}
              fieldName="maxDistributionAmount"
              label="Max distribution amount"
              isDecimalJSInput
              contextualHelp={
                <PopperContent body="Limit the disposition amount across all recipients to a specific dollar value" />
              }
            />
          )}
          {isAsset && (
            <>
              <FormAwarePercentInput<SplitDispositionFormShape>
                control={control}
                fieldName="dispositionAssetPercentage"
                label="Percentage of asset"
                isDecimalJSInput
                decimalScale={5}
                fixedDecimalScale={false}
                contextualHelp={
                  <PopperContent body="Specify what percentage of the selected assets to distribute, split evenly across recipients" />
                }
              />
              {showTransferToggle && (
                <Stack sx={{ minWidth: 200 }}>
                  <FormAwareButtonGroup<SplitDispositionFormShape>
                    label="Transfer"
                    fieldName="transferMethod"
                    control={control}
                    options={TRANSFER_METHOD_OPTIONS}
                    contextualHelp={
                      <PopperContent body="In kind transfer assumes any characteristics, like discounts and growth rates, will carry through the death event; Liquidate assumes cash will be transferred and characteristics will not carry through the death event." />
                    }
                  />
                </Stack>
              )}
            </>
          )}
        </Stack>
      </Card>
    </Collapse>
  );
}
